import { mountContactUs } from '@digital-platforms-web/contact-us';
import { renderFinished } from '@ui5/webcomponents-base';
import { footerReady } from '../../blocks/footer/utils/footer-ready.js';
import { cookieEnabled } from '../../scripts/utils/consent-utils.js';
import {
  CHAT_CONSENT_COOKIE,
  CONTACT_US_ANALYTICS_EVENT,
  CONTACT_US_MANAGE_CONSENT_EVENT,
  UDEX_FOOTER_CONTACT_SELECTOR,
  UDEX_FOOTER_TAG,
} from './constants.js';
import {
  enrichContactUrl,
  forwardContactUsAnalytics,
  getFooterContactTarget,
  initContactUsConfig,
  openConsentManager,
  resolveContactVanityUrl,
} from './utils.js';

let contactUsHandle;
let footerContactTargetPromise;
let footerTargetFailureReported = false;

function transformContactUrl(url) {
  return resolveContactVanityUrl(enrichContactUrl(url));
}

function reportFooterTargetFailure() {
  if (footerTargetFailureReported) {
    return;
  }
  footerTargetFailureReported = true;
  const message = `[ContactUs] <${UDEX_FOOTER_TAG}> rendered but "${UDEX_FOOTER_CONTACT_SELECTOR}" was not found`;
  console.error(message); // NOSONAR
}

async function resolveFooterContactTarget() {
  await footerReady;
  await customElements.whenDefined(UDEX_FOOTER_TAG);
  await renderFinished();

  let target = getFooterContactTarget();
  if (!target) {
    // Nested components may schedule a second render.
    await renderFinished();
    target = getFooterContactTarget();
  }

  if (!target) {
    reportFooterTargetFailure();
  }

  return target;
}

function waitForFooterContactTarget() {
  const immediateTarget = getFooterContactTarget();
  if (immediateTarget) {
    return immediateTarget;
  }

  footerContactTargetPromise ??= resolveFooterContactTarget();
  return footerContactTargetPromise;
}

export async function loadContactUsScript() {
  try {
    if (window.self !== window.top || contactUsHandle) {
      return;
    }
  } catch {
    return;
  }

  const config = await initContactUsConfig();
  if (contactUsHandle) {
    return;
  }

  contactUsHandle = mountContactUs(
    {
      flyoutTarget: document.body,
      footerTarget: waitForFooterContactTarget,
    },
    { ...config, transformContactUrl },
  );

  // Re-evaluate consent after preference changes.
  document.addEventListener('setUserConsent', () => {
    contactUsHandle.setConsent?.(cookieEnabled(CHAT_CONSENT_COOKIE));
  });

  // Forward package analytics through the page-view ordering gate.
  document.addEventListener(
    CONTACT_US_ANALYTICS_EVENT,
    forwardContactUsAnalytics,
  );

  // Open the shared TrustArc preference center.
  document.addEventListener(
    CONTACT_US_MANAGE_CONSENT_EVENT,
    openConsentManager,
  );
}
